// FILE: src/components/Attendance.js
import React, { useEffect, useState } from "react";
import { getAuth } from "firebase/auth";
// NOTE: use the events service, not the Events component
import { getAllEvents } from "../services/eventsService";
import {
  markAttendance,
  aiInferAttendance,
  getAttendanceForEvent,
} from "./attendanceService";

export default function Attendance() {
  const [events, setEvents] = useState([]);
  const [selected, setSelected] = useState("");
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);

  const auth = getAuth();

  // Load events
  useEffect(() => {
    (async () => {
      try {
        const e = await getAllEvents();
        setEvents(e || []);
      } catch (err) {
        console.error("Failed to load events:", err);
        setEvents([]);
      }
    })();
  }, []);

  // Load attendance for selected event
  const loadRecords = async (eventId) => {
    if (!eventId) {
      setRecords([]);
      return;
    }
    setLoading(true);
    try {
      const rec = await getAttendanceForEvent(eventId);
      setRecords(rec || []);
    } catch (err) {
      console.error("Failed to load attendance:", err);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  // Manual mark — default to the current signed-in user
  const handleManualMark = async () => {
    if (!selected) return alert("Select an event first.");

    // ensure user is signed in
    const user = auth.currentUser;
    if (!user) return alert("You are not signed in. Please refresh and try again.");

    const promptInput = prompt(
      `Enter Member ID to mark (leave blank to mark yourself: ${user.uid}).\n\nNOTE: marking another member requires admin privileges.`
    );

    const memberIdToUse = (promptInput && promptInput.trim()) ? promptInput.trim() : user.uid;

    try {
      await markAttendance(memberIdToUse, selected, "manual");
      await loadRecords(selected);
      alert("Attendance marked.");
    } catch (err) {
      console.error("Attendance write failed:", err);
      alert("Failed to mark attendance. Check console for details.");
    }
  };

  // AI inference stub
  const handleAI = async () => {
    if (!selected) return alert("Select an event first.");

    const user = auth.currentUser;
    if (!user) return alert("You must be signed in to run AI inference.");

    try {
      const results = await aiInferAttendance();
      for (const r of results) {
        const memberIdToUse = r.memberId || user.uid;
        try {
          await markAttendance(memberIdToUse, selected, "ai");
        } catch (err) {
          console.error("AI attendance write failed for", memberIdToUse, err);
        }
      }
      await loadRecords(selected);
      alert("AI inference complete. See console for details.");
      console.log("AI inference:", results);
    } catch (err) {
      console.error("AI inference overall failed:", err);
      alert("AI inference failed. See console.");
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h2 className="text-2xl font-semibold mb-4">Attendance Tracking</h2>

      {/* Select Event */}
      <div className="mb-4">
        <select
          value={selected}
          onChange={(e) => {
            setSelected(e.target.value);
            loadRecords(e.target.value);
          }}
          className="p-2 border rounded w-full md:w-1/2"
        >
          <option value="">Select an event</option>
          {events.map((ev) => (
            <option key={ev.id} value={ev.id}>
              {ev.title}
            </option>
          ))}
        </select>
      </div>

      {/* Buttons */}
      <div className="flex gap-3 mb-4">
        <button
          className="px-3 py-1 border rounded bg-white shadow"
          onClick={handleManualMark}
        >
          Mark by ID / Me
        </button>
        <button
          className="px-3 py-1 border rounded bg-indigo-50 shadow"
          onClick={handleAI}
        >
          Run AI Inference (demo)
        </button>
      </div>

      {/* Attendance List */}
      <h3 className="text-lg font-medium mb-2">Attendance Records</h3>

      {loading ? (
        <div>Loading attendance…</div>
      ) : records.length === 0 ? (
        <div className="text-gray-500">No records yet</div>
      ) : (
        <ul className="space-y-2">
          {records.map((r) => (
            <li key={r.id} className="border p-2 rounded bg-white shadow-sm">
              <div>
                <strong>{r.memberId}</strong> — {r.method}
              </div>
              <div className="text-sm text-gray-500">
                {r.createdAt?.seconds
                  ? new Date(r.createdAt.seconds * 1000).toLocaleString()
                  : String(r.createdAt)}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
